import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import supabase from '../../supabaseClient';
import Loader from '../Loader';

/**
 * Compact email + password sign-in for portal login pages.
 */
export default function PortalPasswordLogin({ onError, onSuccessMessage, theme = 'dark' }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const isLight = theme === 'light';
  const inputCls = isLight
    ? 'w-full h-12 px-4 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-colors'
    : 'w-full h-12 px-4 rounded-xl bg-white/[0.08] border-2 border-white/20 text-white text-sm placeholder:text-white focus:outline-none focus:border-white/30 focus:bg-white/[0.1] transition-colors';
  const eyeCls = isLight
    ? 'absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700'
    : 'absolute right-3 top-1/2 -translate-y-1/2 text-white transition hover:text-white/80';
  const submitCls = isLight
    ? 'inline-flex h-12 w-full items-center justify-center rounded-xl bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50'
    : 'inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--signup-accent)] text-sm font-medium text-white transition hover:bg-[var(--signup-accent-hover)] disabled:opacity-50';

  const handleLogin = async (e) => {
    e.preventDefault();
    onError?.('');
    onSuccessMessage?.('');
    setLoading(true);

    try {
      const email = username.trim().toLowerCase();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        onError?.('Invalid email or password');
        return;
      }
    } catch (err) {
      onError?.(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-3">
      <input
        type="email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
        autoComplete="username"
        placeholder="Email"
        className={inputCls}
      />
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          placeholder="Password"
          className={`${inputCls} pr-11`}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          className={eyeCls}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <button
        type="submit"
        disabled={loading}
        className={submitCls}
      >
        {loading ? <Loader size={20} /> : 'Sign in'}
      </button>
    </form>
  );
}
